// Administration → Moteurs → « Connexion des comptes » (docs/wip/SPEC-CONNEXIONS.md, section
// Écran): one card per account — Claude, ChatGPT (Codex), Gemini — with its state, the login
// windows (Claude: open the authorisation page, paste the code back; Codex: a code to type on the
// OpenAI page, detected by polling), logout, the Gemini and Kie.ai key forms and the manual
// Terminal procedure.
// Every timer (job polling every 2 s, the Codex countdown every second) belongs to the open login
// window: it is cleared when the job reaches a final state, when the window closes and on
// destroy(). A login still running that no window follows is read again every 20 s (one timer,
// cleared on destroy()). Secrets (Claude code, API keys) leave their field as soon as they are sent.
import { h, button, input, field, badge, statusDot, setAttr, setText } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { errorText } from './model.js';
import {
  normalizeAccounts, looksLikeAccounts, accountView, unwrapJob, localJob, isJobActive, isJobCancellable,
  jobMessage, safeLoginUrl, formatCountdown, codexCodeExpiry, validateLoginCode, validateGeminiKey, validateKieKey,
  manualSteps, localSteps, planLabel, LOGIN_ENGINES, KEY_ENGINES, GEMINI_KEY_URL, KIE_KEY_URL, CODE_MAX,
} from './accounts-model.js';
import { loadingState, commandLine, copyButton, setBusy, focusKeeper, aid, alertBox, externalLink, setLabel } from './common.js';
import { normalizeConnectors, REFRESH_TIMEOUT_MS as CONNECTORS_REFRESH_TIMEOUT_MS } from './connectors.js';
import './strings.js';

/** Job polling period while a login window is open. */
export const POLL_MS = 2000;
/** Countdown refresh of the Codex window. */
const TICK_MS = 1000;
/** The server waits up to 20 s for the CLI's answer to a pasted code. */
export const CODE_TIMEOUT_MS = 30_000;
/** GET ?refresh=1 runs `claude auth status` (15 s at most) and `codex login status`. */
const LOAD_TIMEOUT_MS = 45_000;
/** A logout runs the CLI's logout (15 s at most, plus the kill grace), then reads both accounts again. */
export const LOGOUT_TIMEOUT_MS = 60_000;
/** Re-read period while a login runs that no window of this page follows (closed while checking…). */
export const WATCH_MS = 20_000;
/** Consecutive failed polls before a login window gives up. */
const MAX_POLL_ERRORS = 5;
const FAILED_STATES = ['error', 'expired', 'cancelled'];
const LIVE_TONES = ['info', 'error', 'success', 'warn'];

/** "marie@example.com · Max" for the account a job connected. */
function whoText(account) {
  return account ? [account.email, planLabel(account.subscription)].filter(Boolean).join(' · ') : '';
}

/**
 * Creates the « Connexion des comptes » section (mounted at the top of the Moteurs tab).
 * @param {object} ctx app context
 * @param {{onChange?: () => void, onData?: (data: object) => void, pollMs?: number, watchMs?: number, now?: () => number}} [opts]
 *   onChange: called after a login, logout or key change (the tab re-reads the engines' status);
 *   onData: called with each accounts state read (the tab adapts its help to a hosted instance);
 *   pollMs, watchMs and now are for tests
 * @returns {{el: HTMLElement, reload(opts?: {force?: boolean}): Promise<void>,
 *   openLogin(engine: string): object|null, destroy(): void}}
 */
export function createAccountsSection(ctx, { onChange, onData, pollMs = POLL_MS, watchMs = WATCH_MS, now = () => Date.now() } = {}) {
  let data = null;
  let error = null;
  let destroyed = false;
  let loadSeq = 0;
  let watchTimer = null;
  /** The open login window: {engine, layer}. */
  let login = null;
  const busy = new Set();
  /** Ids of the finished logins already reported through onChange. */
  const reported = new Set();
  const keeper = focusKeeper();

  const titleId = aid('acc');
  const intro = h('p', { class: 'adm-acc-intro' }, t('adm.acc.intro'));
  const notes = h('div', { class: 'adm-acc-notes' });
  const body = h('div', { class: 'adm-acc-body' }, loadingState());
  const el = h('section', { class: 'adm-acc', 'aria-labelledby': titleId },
    h('div', { class: 'adm-acc-headtext' },
      h('h4', { class: 'adm-acc-title', id: titleId }, t('adm.acc.title')),
      intro),
    notes, body);

  // The key forms (Gemini, Kie.ai) and the manual procedures are built once, so a re-render keeps
  // what is typed in a key field and whether a procedure is unfolded. `new-password`: browsers
  // ignore `off` on a password field and would offer the office password here (or save the key as
  // one); the data-* attributes keep the usual password managers away too.
  const KEY_CARDS = {
    gemini: { validate: validateGeminiKey, url: GEMINI_KEY_URL, path: '/api/engines/gemini-key' },
    kie: { validate: validateKieKey, url: KIE_KEY_URL, path: '/api/engines/kie-key' },
  };
  function keyFormOf(engine) {
    const keyInput = input({
      type: 'password', name: `${engine}-key`, autocomplete: 'new-password', autocapitalize: 'none', spellcheck: false,
      maxLength: 200, placeholder: t(`adm.acc.${engine}.keyPlaceholder`),
      'data-1p-ignore': '', 'data-lpignore': 'true', 'data-bwignore': '',
    });
    const saveKeyBtn = button(t('common.save'), { variant: 'primary', type: 'submit' });
    const keyRow = h('div', { class: 'adm-acc-inline' }, keyInput, saveKeyBtn);
    keyRow.input = keyInput;
    const keyField = field(t(`adm.acc.${engine}.keyLabel`), keyRow, { hint: t(`adm.acc.${engine}.keyHint`) });
    const form = h('form', { class: 'adm-acc-form', novalidate: true, onSubmit: (e) => saveKey(engine, e) }, keyField);
    return { input: keyInput, save: saveKeyBtn, field: keyField, form };
  }
  const keyForms = Object.fromEntries(KEY_ENGINES.map((engine) => [engine, keyFormOf(engine)]));

  /** A Terminal procedure (steps with copy buttons), folded unless `open`. */
  function procedure(title, introText, steps, doc, { open = false } = {}) {
    return h('details', { class: 'adm-eng-help adm-acc-manual', open },
      h('summary', { class: 'adm-eng-help-summary' }, title),
      h('p', { class: 'adm-eng-step-hint adm-acc-manual-intro' }, introText),
      h('ol', { class: 'adm-eng-steps' }, steps.map((step) => h('li', { class: 'adm-eng-step' },
        h('span', { class: 'adm-eng-step-title mono-label' }, step.title),
        step.hint ? h('p', { class: 'adm-eng-step-hint' }, step.hint) : null,
        (step.commands || []).map((cmd) => commandLine(cmd, ctx))))),
      h('p', { class: 'adm-eng-doc muted' }, doc));
  }
  // Hosted instance: the `railway ssh` fallback. Logins off (Mac mini): the Terminal procedure
  // takes the place of the buttons, unfolded.
  const manual = procedure(t('adm.acc.manual.title'), t('adm.acc.manual.intro'), manualSteps(), t('adm.acc.manual.doc'));
  const localManual = procedure(t('adm.acc.local.title'), t('adm.acc.local.intro'), localSteps(), t('adm.acc.local.doc'), { open: true });

  // --- data ------------------------------------------------------------------------------------
  async function load({ force = false } = {}) {
    const mine = ++loadSeq;
    try {
      const res = await ctx.api.get(force ? '/api/engines/accounts?refresh=1' : '/api/engines/accounts', { timeoutMs: LOAD_TIMEOUT_MS });
      if (destroyed || mine !== loadSeq) return;
      setData(normalizeAccounts(res));
      error = null;
    } catch (err) {
      if (destroyed || mine !== loadSeq) return;
      // A quiet reload that fails keeps what is shown.
      if (!data) error = err;
    }
    render();
    scheduleWatch();
  }

  /** Shows the accounts state returned by an action (logout, gemini-key, kie-key), else reads it again. */
  function useAccounts(res) {
    if (!looksLikeAccounts(res)) { load({ force: true }); return; }
    loadSeq += 1; // an older GET still on its way must not overwrite this
    setData(normalizeAccounts(res));
    error = null;
    render();
    scheduleWatch();
  }

  /** Takes a new accounts state; a login that ended well while no window followed it is reported. */
  function setData(next) {
    const before = data;
    data = next;
    try { onData?.(next); } catch { /* the tab's own rendering problem */ }
    for (const engine of LOGIN_ENGINES) {
      const was = before?.jobs[engine];
      const job = next.jobs[engine];
      if (isJobActive(was) && job?.state === 'done' && job.id === was.id) reportDone(job);
    }
  }

  /** onChange once per finished login (the window and a re-read may both see it end). */
  function reportDone(job) {
    if (job.id && reported.has(job.id)) return;
    if (job.id) reported.add(job.id);
    onChange?.();
  }

  /**
   * A login still running that no window of this page follows (the window was closed while the
   * login was being checked, or another tab started it): read the accounts again in WATCH_MS, so
   * the card does not say « Une connexion est en cours » forever. One timer at a time.
   */
  function scheduleWatch() {
    clearTimeout(watchTimer);
    watchTimer = null;
    if (destroyed || !data) return;
    if (!LOGIN_ENGINES.some((engine) => isJobActive(data.jobs[engine]) && login?.engine !== engine)) return;
    watchTimer = setTimeout(() => { watchTimer = null; load(); }, watchMs);
  }

  // --- rendering -------------------------------------------------------------------------------
  function render() {
    if (destroyed) return;
    const restore = keeper.begin();
    if (!data) {
      notes.replaceChildren();
      if (error?.status === 404) body.replaceChildren(alertBox('info', t('adm.acc.unavailable')));
      else if (error) {
        body.replaceChildren(h('div', { class: 'adm-acc-error' },
          alertBox('danger', t('adm.acc.loadError', { error: errorText(error) })),
          button(t('adm.retry'), { variant: 'secondary', size: 'sm', icon: 'refresh', onClick: () => { error = null; body.replaceChildren(loadingState()); load(); } })));
      } else body.replaceChildren(loadingState());
      return;
    }
    const d = data;
    const local = !d.mock && !d.loginsEnabled;
    setText(intro, t(local ? 'adm.acc.introLocal' : 'adm.acc.intro'));
    notes.replaceChildren(...[
      d.mock ? alertBox('info', t('adm.acc.mock')) : null,
      local ? alertBox('info', t('adm.acc.local')) : null,
    ].filter(Boolean));
    body.replaceChildren(...[
      h('div', { class: 'adm-acc-grid' }, loginCard('claude', d), loginCard('codex', d), ...KEY_ENGINES.map((engine) => keyCard(engine, d))),
      d.loginsEnabled && !d.mock ? manual : null,
      local ? localManual : null,
    ].filter(Boolean));
    restore();
  }

  function cardHead(name, v) {
    return h('header', { class: 'adm-acc-head' },
      h('h5', { class: 'adm-acc-name' }, name),
      h('span', { class: 'adm-acc-state' }, statusDot(v.dot), badge(v.badge, v.tone)));
  }

  function loginCard(engine, d) {
    const v = accountView(engine, d[engine], { loginsEnabled: d.loginsEnabled, mock: d.mock, job: d.jobs[engine], now: now(), verified: ctx.store.get().engines?.[engine]?.state ?? null });
    const name = t(`adm.acc.name.${engine}`);
    const actions = [];
    if (v.connect) {
      actions.push(keeper.reg(`connect:${engine}`, button(v.connect, {
        variant: 'primary', size: 'sm', icon: 'key', ariaLabel: v.connectAria, onClick: () => openLogin(engine),
      })));
    }
    if (v.logout) {
      const btn = keeper.reg(`logout:${engine}`, button(t('adm.acc.logout'), {
        variant: 'secondary', size: 'sm', icon: 'logout', ariaLabel: t('adm.acc.logoutAria', { name }),
        onClick: () => logout(engine),
      }));
      if (busy.has(`logout:${engine}`)) setBusy(btn, true);
      actions.push(btn);
    }
    const renewHint = t(d.loginsEnabled ? 'adm.acc.renew.hint' : 'adm.acc.renew.hintLocal');
    return h('article', { class: ['adm-acc-card', `is-${v.tone}`], dataset: { account: engine } },
      cardHead(name, v),
      h('p', { class: 'adm-acc-status' }, v.text),
      v.detail ? h('p', { class: 'adm-acc-detail muted' }, v.detail) : null,
      v.renew ? alertBox(v.renew.expired ? 'danger' : 'amber', h('strong', {}, v.renew.text), ' — ', renewHint) : null,
      v.tokenVar ? alertBox('danger', t(d.loginsEnabled ? 'adm.acc.claude.tokenVar' : 'adm.acc.claude.tokenVarLocal')) : null,
      v.jobActive && login?.engine !== engine ? h('p', { class: 'adm-acc-detail' }, t('adm.acc.jobActive')) : null,
      actions.length ? h('div', { class: 'adm-acc-actions' }, actions) : null);
  }

  /** Card of an account that takes an API key (Gemini, Kie.ai). */
  function keyCard(engine, d) {
    const v = accountView(engine, d[engine], { loginsEnabled: d.loginsEnabled, mock: d.mock, verified: ctx.store.get().engines?.[engine]?.state ?? null });
    const f = keyForms[engine];
    keeper.reg(`${engine}:key`, f.input);
    keeper.reg(`${engine}:save`, f.save);
    let removeBtn = null;
    if (v.removeKey) {
      removeBtn = keeper.reg(`${engine}:remove`, button(t('common.delete'), {
        variant: 'ghost', size: 'sm', ariaLabel: t(`adm.acc.${engine}.removeAria`), onClick: () => removeKey(engine),
      }));
      if (busy.has(`${engine}:remove`)) setBusy(removeBtn, true);
    }
    return h('article', { class: ['adm-acc-card', `is-${v.tone}`], dataset: { account: engine } },
      cardHead(t(`adm.acc.name.${engine}`), v),
      h('p', { class: 'adm-acc-status' }, v.text),
      v.state === 'env'
        ? h('p', { class: 'adm-acc-detail muted' }, t(d.loginsEnabled ? `adm.acc.${engine}.envHint` : `adm.acc.${engine}.envHintLocal`))
        : null,
      v.alsoStored ? h('p', { class: 'adm-acc-detail muted' }, t(`adm.acc.${engine}.alsoStored`)) : null,
      v.editKey ? f.form : null,
      removeBtn ? h('div', { class: 'adm-acc-actions' }, removeBtn) : null,
      h('p', { class: 'adm-acc-links' }, externalLink(KEY_CARDS[engine].url, t(`adm.acc.${engine}.createKey`))),
      h('p', { class: 'adm-acc-detail muted' }, t(`adm.acc.${engine}.free`)));
  }

  // --- actions ---------------------------------------------------------------------------------
  async function logout(engine) {
    const key = `logout:${engine}`;
    if (busy.has(key) || destroyed) return;
    const name = t(`adm.acc.name.${engine}`);
    const ok = await ctx.overlay.confirm({
      title: t('adm.acc.logoutTitle', { name }), message: t(`adm.acc.logoutText.${engine}`),
      confirmLabel: t('adm.acc.logout'), danger: true,
    });
    if (!ok || destroyed) return;
    busy.add(key);
    render();
    try {
      const res = await ctx.api.post(`/api/engines/logout/${engine}`, {}, { timeoutMs: LOGOUT_TIMEOUT_MS });
      if (destroyed) return;
      busy.delete(key);
      ctx.toast.success(t('adm.acc.logoutDone', { name }));
      useAccounts(res);
      onChange?.();
    } catch (err) {
      // 409: agents are working (the server says so in French).
      if (!destroyed) ctx.toast.error(errorText(err));
    } finally {
      if (busy.delete(key) && !destroyed) render();
    }
  }

  async function saveKey(engine, e) {
    e?.preventDefault?.();
    const f = keyForms[engine];
    if (busy.has(`${engine}:save`) || destroyed) return;
    const checked = KEY_CARDS[engine].validate(f.input.value);
    if (!checked.ok) {
      f.field.setError(checked.error);
      f.input.focus();
      return;
    }
    f.field.setError(null);
    // The key only lives in this request from now on: the field is emptied before sending.
    f.input.value = '';
    busy.add(`${engine}:save`);
    setBusy(f.save, true);
    try {
      const res = await ctx.api.put(KEY_CARDS[engine].path, { key: checked.value });
      if (destroyed) return;
      useAccounts(res);
      // A variable of the environment passes before a saved key: say so rather than « enregistrée ».
      if (data?.[engine]?.state === 'env') ctx.toast.warn(t(`adm.acc.${engine}.savedEnv`, { env: data[engine].envName }));
      else ctx.toast.success(t(`adm.acc.${engine}.saved`));
      onChange?.();
    } catch (err) {
      if (!destroyed) {
        f.field.setError(errorText(err));
        f.input.focus();
      }
    } finally {
      busy.delete(`${engine}:save`);
      setBusy(f.save, false);
    }
  }

  async function removeKey(engine) {
    const id = `${engine}:remove`;
    if (busy.has(id) || destroyed) return;
    const ok = await ctx.overlay.confirm({
      title: t(`adm.acc.${engine}.removeTitle`), message: t(`adm.acc.${engine}.removeText`),
      confirmLabel: t('common.delete'), danger: true,
    });
    if (!ok || destroyed) return;
    busy.add(id);
    render();
    try {
      const res = await ctx.api.del(KEY_CARDS[engine].path);
      if (destroyed) return;
      busy.delete(id);
      ctx.toast.success(t(`adm.acc.${engine}.removed`));
      useAccounts(res);
      onChange?.();
    } catch (err) {
      if (!destroyed) ctx.toast.error(errorText(err));
    } finally {
      if (busy.delete(id) && !destroyed) render();
    }
  }

  async function refreshConnectors(btn) {
    setBusy(btn, true);
    try {
      const res = await ctx.api.post('/api/connectors/refresh', {}, { timeoutMs: CONNECTORS_REFRESH_TIMEOUT_MS });
      if (destroyed) return;
      const out = normalizeConnectors(res);
      if (out.lastError) ctx.toast.warn(t('adm.conn.lastError', { error: out.lastError }));
      else ctx.toast.success(t('adm.conn.refreshed', { n: out.servers.length }));
    } catch (err) {
      if (!destroyed) ctx.toast.error(errorText(err));
    } finally {
      setBusy(btn, false);
    }
  }

  // --- login windows ---------------------------------------------------------------------------
  /**
   * Opens the login window of `engine` (POST /api/engines/login/:engine, which hands back the
   * active job when there is one) and follows the job until it ends.
   * @param {'claude'|'codex'} engine
   * @returns {{engine: string, layer: object}|null}
   */
  function openLogin(engine) {
    if (destroyed || !data?.loginsEnabled || data.mock || !LOGIN_ENGINES.includes(engine)) return null;
    login?.layer.close('api');
    let job = localJob(engine, 'starting');
    let seq = 0; // bumped by every change of `job`: a poll answer older than the last change is dropped
    let pollTimer = null;
    let pollErrors = 0;
    let pendingStart = false;
    let sending = false;
    let closed = false;

    const spin = h('span', { class: 'spinner adm-acc-spin', 'aria-hidden': 'true' });
    const live = h('p', { class: 'adm-acc-live', role: 'status', 'aria-live': 'polite' });
    const view = engine === 'claude' ? claudeView() : codexView();
    const closeBtn = button(t('common.cancel'), { variant: 'ghost', onClick: () => layer.close('button') });
    // Not dismissible: a stray click on the backdrop or Esc (after a trip to the login page) would
    // cancel the login; only the footer button (Annuler / Fermer) closes the window.
    const layer = ctx.overlay.openModal({
      title: t(`adm.acc.${engine}.title`), subtitle: t(`adm.acc.${engine}.subtitle`), size: 'md',
      content: h('div', { class: 'stack adm-acc-login' }, h('div', { class: 'adm-acc-statusrow' }, spin, live), view.el),
      footer: [closeBtn], className: 'adm-modal adm-acc-modal', dismissible: false,
    });
    const win = { engine, layer };
    login = win;
    scheduleWatch(); // this window follows the job now

    layer.onClose(() => {
      closed = true;
      stopTimers();
      view.wipe();
      if (login === win) login = null;
      // Closing abandons a login still waiting for the admin (the process is killed); a job
      // being checked is left to finish. A start still on its way is cancelled when it answers.
      // Jobs are never cached by the server: a plain read shows where the job is.
      if (!pendingStart && isJobCancellable(job)) cancelJob();
      else if (!destroyed && job.state !== 'done') load();
    });

    function cancelJob() {
      Promise.resolve(ctx.api.del(`/api/engines/login/${engine}`))
        .catch(() => {})
        .then(() => { if (!destroyed) load(); });
    }

    function stopTimers() {
      clearTimeout(pollTimer);
      pollTimer = null;
      view.stop();
    }

    /** Shows `job` (status line, spinner, footer button, engine-specific parts). */
    function show(prev) {
      const msg = jobMessage(job);
      setText(live, msg.text);
      for (const tone of LIVE_TONES) live.classList.toggle(`is-${tone}`, msg.tone === tone);
      spin.hidden = !(job.state === 'starting' || job.state === 'checking');
      setLabel(closeBtn, isJobCancellable(job) ? t('common.cancel') : t('common.close'));
      view.update(job, prev);
    }

    /** Takes a new state of the job and polls while it runs. */
    function apply(next) {
      if (closed) return;
      seq += 1;
      const prev = job;
      job = next || localJob(engine, 'error', t('adm.acc.job.lost'));
      show(prev);
      if (isJobActive(job)) {
        schedulePoll();
        return;
      }
      stopTimers();
      if (job.state === 'done' && prev.state !== 'done') {
        load({ force: true });
        reportDone(job);
      }
    }

    function schedulePoll() {
      clearTimeout(pollTimer);
      pollTimer = null;
      if (closed || destroyed || sending || pendingStart || !isJobActive(job)) return;
      pollTimer = setTimeout(poll, pollMs);
    }

    async function poll() {
      pollTimer = null;
      if (closed || destroyed || sending) return;
      const mine = seq;
      try {
        const res = await ctx.api.get(`/api/engines/login/${engine}`);
        if (closed || sending || mine !== seq) return;
        pollErrors = 0;
        apply(unwrapJob(res, engine));
      } catch (err) {
        if (closed || sending || mine !== seq) return;
        pollErrors += 1;
        if ([401, 403, 404].includes(err.status) || pollErrors >= MAX_POLL_ERRORS) apply(localJob(engine, 'error', errorText(err)));
        else schedulePoll();
      }
    }

    async function start() {
      stopTimers();
      seq += 1;
      const prev = job;
      job = localJob(engine, 'starting');
      show(prev);
      pendingStart = true;
      const mine = seq;
      let next = null;
      let failure = null;
      try {
        next = unwrapJob(await ctx.api.post(`/api/engines/login/${engine}`, {}), engine);
      } catch (err) {
        failure = err;
      }
      pendingStart = false;
      if (closed) {
        if (isJobCancellable(next)) cancelJob();
        return;
      }
      if (mine !== seq) return;
      apply(failure ? localJob(engine, 'error', errorText(failure)) : next || localJob(engine, 'error', t('adm.acc.job.noJob')));
    }

    /** Sends the pasted Claude code (the server answers once the CLI has read it). */
    async function sendCode() {
      if (sending || closed || job.state !== 'waiting_code') return;
      const checked = validateLoginCode(view.codeInput.value);
      if (!checked.ok) {
        view.codeField.setError(checked.error);
        view.codeInput.focus();
        return;
      }
      view.codeField.setError(null);
      view.codeInput.value = '';
      sending = true;
      clearTimeout(pollTimer);
      pollTimer = null;
      const waiting = job;
      seq += 1;
      job = { ...waiting, state: 'checking', message: '' };
      show(waiting);
      let res = null;
      let failure = null;
      try {
        res = await ctx.api.post('/api/engines/login/claude/code', { code: checked.value }, { timeoutMs: CODE_TIMEOUT_MS });
      } catch (err) {
        failure = err;
      }
      sending = false;
      if (closed) return;
      if (!failure) {
        apply(unwrapJob(res, 'claude'));
      } else if (failure.status === 400) {
        apply(waiting);
        view.codeField.setError(errorText(failure));
      } else {
        // 409 (no job waits for a code any more), timeout or network: say so, then read the job.
        ctx.toast.error(errorText(failure));
        seq += 1;
        poll();
      }
    }

    function claudeView() {
      const link = externalLink('', t('adm.acc.claude.openPage'), { primary: true });
      const badUrl = alertBox('danger', t('adm.acc.badUrl'));
      const codeInput = input({
        name: 'claude-code', autocomplete: 'off', autocapitalize: 'none', spellcheck: false, maxLength: CODE_MAX,
      });
      const submitBtn = button(t('adm.acc.claude.submit'), { variant: 'primary', type: 'submit' });
      const row = h('div', { class: 'adm-acc-inline' }, codeInput, submitBtn);
      row.input = codeInput;
      const codeField = field(t('adm.acc.claude.codeLabel'), row, { hint: t('adm.acc.claude.codeHint') });
      const steps = h('ol', { class: 'adm-acc-steps' },
        h('li', { class: 'adm-acc-step' },
          h('span', { class: 'adm-acc-step-title' }, t('adm.acc.claude.step1')),
          h('p', { class: 'adm-acc-step-hint' }, t('adm.acc.claude.step1Hint')),
          link, badUrl),
        h('li', { class: 'adm-acc-step' },
          h('span', { class: 'adm-acc-step-title' }, t('adm.acc.claude.step2')),
          h('form', { class: 'adm-acc-form', novalidate: true, onSubmit: (e) => { e?.preventDefault?.(); sendCode(); } }, codeField)));
      const why = h('p', { class: 'adm-acc-why muted' }, t('adm.acc.claude.why'));
      const who = h('p', { class: 'adm-acc-status' });
      const refreshBtn = button(t('adm.acc.claude.refreshConnectors'), {
        variant: 'primary', icon: 'refresh', onClick: () => refreshConnectors(refreshBtn),
      });
      const connectorsHint = h('p', { class: 'adm-acc-step-hint' }, t('adm.acc.claude.connectorsHint'));
      const done = h('div', { class: 'adm-acc-done' }, who, connectorsHint, refreshBtn);
      const retryBtn = button(t('adm.acc.retry'), { variant: 'primary', icon: 'refresh', onClick: () => start() });
      const failed = h('div', { class: 'adm-acc-failed' }, retryBtn);
      return {
        el: h('div', { class: 'adm-acc-view' }, steps, why, done, failed),
        codeInput,
        codeField,
        update(j, prev) {
          const waiting = j.state === 'waiting_code';
          const checking = j.state === 'checking';
          steps.hidden = !(waiting || checking);
          why.hidden = steps.hidden;
          const url = safeLoginUrl('claude', j.url);
          if (url) setAttr(link, 'href', url); else link.removeAttribute('href');
          link.hidden = !url;
          badUrl.hidden = !!url;
          codeInput.disabled = checking;
          setBusy(submitBtn, checking);
          done.hidden = j.state !== 'done';
          setText(who, whoText(j.account));
          who.hidden = !who.textContent;
          // A caveat (the CLAUDE_CODE_OAUTH_TOKEN variable still set) masks the connectors: no
          // promise about them then.
          const masked = !!j.message || data?.claude.tokenVarSet === true;
          connectorsHint.hidden = masked;
          refreshBtn.hidden = masked;
          failed.hidden = !FAILED_STATES.includes(j.state);
          // The code field takes the focus when step 2 appears and after a refused code — not on
          // every poll, so the admin can go to the link meanwhile.
          if (waiting && prev?.state !== 'waiting_code') codeInput.focus();
          if (!done.hidden && prev?.state !== 'done') (masked ? closeBtn : refreshBtn).focus();
          if (!failed.hidden && !FAILED_STATES.includes(prev?.state)) retryBtn.focus();
        },
        stop() {},
        wipe() { codeInput.value = ''; },
      };
    }

    function codexView() {
      let tickTimer = null;
      // The code's role is read from a hidden label (aria-label is not allowed on <code>).
      const code = h('code', { class: 'adm-acc-usercode', translate: 'no' });
      const copy = copyButton(() => job.userCode, { toast: ctx.toast, size: 'md', variant: 'secondary' });
      const countdown = h('p', { class: 'adm-acc-countdown' });
      const link = externalLink('', t('adm.acc.codex.openPage'), { primary: true });
      const badUrl = alertBox('danger', t('adm.acc.badUrl'));
      const steps = h('ol', { class: 'adm-acc-steps' },
        h('li', { class: 'adm-acc-step' },
          h('span', { class: 'adm-acc-step-title' }, t('adm.acc.codex.step1')),
          h('div', { class: 'adm-acc-coderow' }, h('span', { class: 'sr-only' }, `${t('adm.acc.codex.codeAria')} : `), code, copy),
          countdown),
        h('li', { class: 'adm-acc-step' },
          h('span', { class: 'adm-acc-step-title' }, t('adm.acc.codex.step2')),
          h('p', { class: 'adm-acc-step-hint' }, t('adm.acc.codex.step2Hint')),
          link, badUrl),
        h('li', { class: 'adm-acc-step' }, h('span', { class: 'adm-acc-step-hint' }, t('adm.acc.codex.auto'))));
      const refused = h('p', { class: 'adm-note' }, t('adm.acc.codex.refused'));
      const who = h('p', { class: 'adm-acc-status' });
      const retryBtn = button(t('adm.acc.retry'), { variant: 'primary', icon: 'refresh', onClick: () => start() });
      const failed = h('div', { class: 'adm-acc-failed' }, retryBtn);

      /** Updates the countdown (to the end of the code, not of the job); false once over. */
      function tick() {
        const end = codexCodeExpiry(job);
        if (!end) {
          countdown.hidden = true;
          return false;
        }
        const left = end - now();
        countdown.hidden = false;
        setText(countdown, left > 0 ? t('adm.acc.codex.countdown', { time: formatCountdown(left) }) : t('adm.acc.codex.codeExpired'));
        return left > 0;
      }

      function stop() {
        clearInterval(tickTimer);
        tickTimer = null;
      }

      return {
        el: h('div', { class: 'adm-acc-view' }, steps, refused, who, failed),
        update(j, prev) {
          const waiting = j.state === 'waiting_user';
          steps.hidden = !waiting;
          setText(code, j.userCode || '—');
          const url = safeLoginUrl('codex', j.url);
          if (url) setAttr(link, 'href', url); else link.removeAttribute('href');
          link.hidden = !url;
          badUrl.hidden = !!url;
          refused.hidden = !(waiting || FAILED_STATES.includes(j.state));
          setText(who, j.state === 'done' ? whoText(j.account) : '');
          who.hidden = !who.textContent;
          failed.hidden = !FAILED_STATES.includes(j.state);
          if (waiting && tick()) {
            if (!tickTimer) {
              tickTimer = setInterval(() => { if (!tick()) stop(); }, TICK_MS);
            }
          } else {
            stop();
          }
          if (waiting && prev?.state !== 'waiting_user') copy.focus();
          if (j.state === 'done' && prev?.state !== 'done') closeBtn.focus();
          if (!failed.hidden && !FAILED_STATES.includes(prev?.state)) retryBtn.focus();
        },
        stop,
        wipe() {},
      };
    }

    start();
    return win;
  }

  load();
  // The key cards' dots follow the engines' checks (a key accepted or refused).
  const offEngines = ctx.store.on?.('engines', () => { if (data) render(); });
  return {
    el,
    reload: (opts) => load(opts),
    openLogin,
    destroy() {
      destroyed = true;
      offEngines?.();
      clearTimeout(watchTimer);
      watchTimer = null;
      // Closing the window stops its timers and cancels a login still waiting for the admin.
      login?.layer.close('api');
      login = null;
      for (const f of Object.values(keyForms)) f.input.value = '';
    },
  };
}
